import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    createContentPersonalNoteApi,
    deleteContentPersonalNoteApi,
    fetchContentPersonalNotesApi,
    updateContentPersonalNoteApi,
} from "../../../../services/modulePersonalNotesApi";
import ContentPersonalNoteForm from "./ContentPersonalNoteForm";
import ContentPersonalNoteItem from "./ContentPersonalNoteItem";
import "./ContentPersonalNotes.css";

const extractErrorMessage = (error) =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || "Action impossible pour le moment.";

const contextTypeFromContent = (contentType) => {
    const value = String(contentType || "").toUpperCase();
    if (["VIDEO", "PDF", "SCORM", "ARTICLE", "QUIZ", "EVALUATION"].includes(value)) {
        return value;
    }
    return "GENERAL";
};

export default function ContentPersonalNotesPanel({ moduleId, content, context, onNavigateContext }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const noteContext = useMemo(() => ({
        noteContextType: contextTypeFromContent(context?.contentTypeContext || content?.contentType),
        videoTimestampSeconds: context?.videoTimestampSeconds,
        pdfPage: context?.pdfPage,
        scormNodeId: context?.scormNodeId,
        scormNodeTitle: context?.scormNodeTitle,
        scrollPosition: context?.scrollPosition,
    }), [content?.contentType, context]);

    const loadNotes = useCallback(async () => {
        if (!moduleId || !content?.id) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchContentPersonalNotesApi(moduleId, content.id);
            setNotes(response.data || []);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [content?.id, moduleId]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    const handleCreate = async (payload) => {
        setSubmitting(true);
        setError(null);
        try {
            await createContentPersonalNoteApi(moduleId, content.id, payload);
            await loadNotes();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (noteId, payload) => {
        setSubmitting(true);
        setError(null);
        try {
            await updateContentPersonalNoteApi(moduleId, content.id, noteId, payload);
            await loadNotes();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (noteId) => {
        setSubmitting(true);
        setError(null);
        try {
            await deleteContentPersonalNoteApi(moduleId, content.id, noteId);
            await loadNotes();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pn-panel">
            <div className="pn-toolbar">
                <div>
                    <h3>Mes notes</h3>
                    <p>{content?.title || "Contenu courant"}</p>
                </div>
                <span className="pn-private">Privé</span>
            </div>

            <ContentPersonalNoteForm
                context={noteContext}
                submitting={submitting}
                onSubmit={handleCreate}
            />

            {error ? <div className="pn-state pn-state--error">{error}</div> : null}
            {loading ? <div className="pn-state">Chargement des notes...</div> : null}

            {!loading && notes.length === 0 ? (
                <div className="pn-empty">Aucune note personnelle pour ce contenu.</div>
            ) : null}

            <div className="pn-list">
                {notes.map((note) => (
                    <ContentPersonalNoteItem
                        key={note.id}
                        note={note}
                        context={noteContext}
                        submitting={submitting}
                        onNavigate={onNavigateContext}
                        onUpdate={handleUpdate}
                        onDelete={handleDelete}
                    />
                ))}
            </div>
        </div>
    );
}
